import * as React from 'react'
import { cn } from '../../lib/cn'

/**
 * HintStrip — horizontal legend strip at the bottom of the editor.
 *
 * Renders the editor's visual taxonomy (sys / both / signin / signup
 * / per-intent / containers / custom / opt) as inline items: a small
 * 8x8 swatch followed by the uppercase mono label.
 *
 * Source of truth: `mockups/unified-pipeline.html` `.hint-strip`.
 */
export interface HintStripItem {
  /** Legend label. */
  label: React.ReactNode
  /** Swatch fill color (defaults to a subtle hairline). Ignored when
   *  `dashed` is true. */
  swatchColor?: string
  /** When true, render a transparent swatch with a dashed border. */
  dashed?: boolean
}

export interface HintStripProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Legend items rendered left-to-right. */
  items: HintStripItem[]
}

export const HintStrip = React.forwardRef<HTMLDivElement, HintStripProps>(
  function HintStrip({ className, items, ...rest }, ref) {
    return (
      <div
        ref={ref}
        className={cn(
          'flex items-center gap-4 px-7 h-9 border-t overflow-x-auto whitespace-nowrap',
          '[scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
          'font-mono text-[10.5px]',
          className,
        )}
        style={{
          background: 'color-mix(in srgb, var(--pe-bg-canvas) 80%, transparent)',
          borderColor: 'var(--pe-border-subtle)',
          color: 'var(--pe-text-tertiary)',
        }}
        // tabIndex makes the horizontally-scrollable strip reachable via
        // keyboard — required by axe `scrollable-region-focusable`.
        tabIndex={0}
        role="list"
        aria-label="Pipeline legend"
        data-testid="pipeline-hint-strip"
        {...rest}
      >
        {items.map((item, idx) => (
          <span
            key={idx}
            role="listitem"
            className="inline-flex items-center gap-1.5"
            data-testid="pipeline-hint-strip-item"
            data-swatch={item.dashed ? 'dashed' : 'solid'}
          >
            <span
              aria-hidden
              className="w-2 h-2 shrink-0 rounded-[2px]"
              style={
                item.dashed
                  ? {
                      background: 'transparent',
                      border: '1px dashed var(--pe-border-sys)',
                    }
                  : { background: item.swatchColor ?? 'var(--pe-text-tertiary)' }
              }
            />
            {item.label}
          </span>
        ))}
      </div>
    )
  },
)
